// Maps the React app's nested store shape into the JSON shape that the
// Salesforce FinancialProfileService Apex endpoint expects.
//
// Source-of-truth for the target shape:
//   PQ Intake form sample JSON shared by Srivani (latest version).

import { getDashboardMetrics, getTotalExpenses, ownerPerson } from './selectors.js';
import { pqSections } from '../schema/pqSections.js';
import { serializeLog } from './planningLog.js';
import { MIN_DATE, MAX_DATE, NUMERIC_RE } from '../utils/validation.js';
import { SF_TEXT_LIMITS, SF_WHOLE_NUMBER_FIELDS } from '../schema/fieldLimits.js';

const s = v => {
  if (v === undefined || v === null) return '';
  const str = String(v);
  if (str === 'N/A' || str === 'n/a') return '';
  return str;
};
// Outer year bounds, taken from the same MIN_DATE/MAX_DATE the field-level
// validation uses, so there is one definition of "a date this app accepts"
// rather than two that can drift apart.
const MIN_YEAR = Number(MIN_DATE.slice(0, 4));
const MAX_YEAR = Number(MAX_DATE.slice(0, 4));

// Dates: null unless the value is a complete, real calendar date.
//
// A date input reports its value on every keystroke, so a half-typed year
// arrives here as 0002-01-03 or 0200-01-02 on the way to 2003 or 2020. This
// used to pass straight through, and Salesforce refused the whole upsert with
// FIELD_INTEGRITY_EXCEPTION — so one advisor mid-way through typing a year
// stopped the ENTIRE record saving, for as long as the part-typed value sat
// there. Two real cases on 2026-08-26, Issue_Date__c and Retirement_Date__c,
// cost an advisor a session's work (Jimmy, same day).
//
// The UI already flags these (isDateOutOfRange marks the field red); it just
// sent them regardless. Holding the value back until it is a whole date lets
// every other field on the form keep saving while the advisor finishes typing,
// and the moment the year is complete the real value goes with the next save.
// The advisor still sees what they typed: this filters the PAYLOAD, not the
// store.
const d = v => {
  if (v === undefined || v === null || v === '' || v === 'N/A') return null;
  const str = String(v).trim();
  // Four year digits is necessary but NOT sufficient: 0200-01-02 is four
  // digits and a real calendar date (year 200 AD), and it is exactly what the
  // production failure looked like on the way to typing 2003. So the year is
  // also bounded to the range this app already considers a date at all.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (year < MIN_YEAR || year > MAX_YEAR) return null;
  // Calendar-aware, so 2025-02-30 is withheld too rather than being silently
  // rolled forward by Salesforce into a date the advisor never entered.
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
    return null;
  }
  return str;
};
// Numeric: null when empty/invalid, otherwise pass through as string so Apex
// can JSON.deserialize into Decimal/Integer.
// Numeric: null when empty or not a clean number, otherwise passed through as
// a string so Apex can JSON.deserialize into Decimal/Integer.
//
// This used to test `isNaN(parseFloat(str))`, which accepts anything that
// STARTS with a number: '3%' parses as 3, so the string '3%' went up in the
// payload and Apex refused to deserialize it, failing the entire save. Same
// shape of failure as the half-typed dates above, same remedy — withhold the
// value, keep the rest of the record saving, and let the field's own red flag
// tell the advisor (isNotNumeric drives that flag from the same regex).
const n = v => {
  if (v === undefined || v === null || v === '' || v === 'N/A') return null;
  const str = String(v).replace(/,/g, '').trim();
  if (!NUMERIC_RE.test(str)) return null;
  return str;
};
// Numeric, but only if it falls within [min, max]; otherwise null. The UI flags
// out-of-range values (negative age, 200, etc.) but does not block auto-save, so
// this is the boundary that stops garbage from persisting to Salesforce (QA #2:
// a -99 age round-tripped). Salesforce validation rules are the final backstop.
const nBounded = (v, min, max) => {
  const val = n(v);
  if (val === null) return null;
  const f = parseFloat(val);
  if ((min != null && f < min) || (max != null && f > max)) return null;
  return val;
};
// Text past a Salesforce field's length is withheld, not truncated.
//
// Same reasoning as the date guard above: the upsert is all-or-nothing, so one
// over-length value stops the entire record saving. Two advisors hit this on
// the same morning on Text(4) year fields, "2022 - Inh" and "2000?" (Jimmy,
// 2026-08-28). Both slipped through n(): parseFloat("2000?") is 2000, so the
// numeric check passes and n() returns the ORIGINAL string, junk and all.
//
// The inputs carry maxLength now, so this is the backstop for values already in
// a record from before that existed. Withholding blanks the field rather than
// preserving Salesforce's copy, which is a real cost. It is the right trade
// anyway: a value long enough to reach here has never been accepted, so there
// is no copy to preserve, and the alternative is the advisor's whole session
// failing to save. The UI flags the field red, so this is a visible hold rather
// than a silent drop.
//
// Truncating would be worse: "2022 - Inh" would land as "2022" and quietly
// assert something the advisor did not say.
//
// `sfField` is the Salesforce API name, so each cap is tied to the field it
// actually protects. Keying by our own column names instead would apply an
// asset's limit to every same-named column on other objects, several of which
// have no limit at all.
const cap = (v, sfField) => {
  const max = SF_TEXT_LIMITS[sfField];
  if (v === null || v === undefined) return v;
  return String(v).length > max ? null : v;
};
// Same, for fields sent as '' rather than null when absent.
const capS = (v, sfField) => (String(v).length > SF_TEXT_LIMITS[sfField] ? '' : v);

// A fraction on a whole-number Salesforce field is withheld, not rounded.
//
// The third variant of the same failure, after the date guard and the length
// cap. `Full_Retirement_Age__c` and friends are scale 0, and Apex refuses a
// decimal in the JSON DESERIALIZER — before any field-level validation — so
// the whole upsert dies and the error names only a byte offset:
//
//   For input string: "66.4" at [line:1, column:4234]
//
// `nBounded` did not catch it. It checks the RANGE (0-120, which 66.4 passes)
// and then returns the original string with the decimal intact.
//
// Rounding would be worse than withholding, and this is the clearest case of
// it yet: `66.4` is Social Security's OWN phrasing, "66 and 4 months". Sending
// 66 would silently drop four months of an advisor's deliberate precision and
// look correct doing it. Withholding leaves the field empty, flags it red, and
// lets the advisor decide.
const whole = (v, sfField) => {
  if (!SF_WHOLE_NUMBER_FIELDS[sfField]) return v;
  if (v === null || v === undefined || v === '') return v;
  const num = Number(String(v).trim());
  if (!Number.isFinite(num)) return v;   // not our failure to fix
  return Number.isInteger(num) ? v : null;
};

const uid = r => s(r?._uid);
// Explicit boolean, never a truthy string/undefined, so Apex can deserialize
// straight into Boolean.
const bool = v => !!v;

// True when a row holds at least one user-entered value. Internal bookkeeping
// keys (_uid, _autoKey, _source, _reKey, ...) don't count. Used to drop blank
// rows so they don't create empty Salesforce child records.
function rowHasData(r) {
  if (!r || typeof r !== 'object') return false;
  return Object.keys(r).some(k => {
    if (k.startsWith('_')) return false;
    const v = r[k];
    if (v === null || v === undefined) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  });
}

function mapChildren(data) {
  const fam = data.family || {};
  const children = (Array.isArray(fam.children) ? fam.children : []).filter(rowHasData);
  const grandchildren = (Array.isArray(fam.grandchildren) ? fam.grandchildren : []).filter(rowHasData);
  return [
    ...children.map(c => ({
      uid: uid(c),
      relationship: 'children',
      name: s(c?.name),
      age: nBounded(c?.age, 0, 120),
      // Per-row advisor note (the note icon). Jake's example: "Brett lives at
      // home, may go into the military in September." Pending SF field.
      notes: s(c?.notes),
    })),
    ...grandchildren.map(g => ({
      uid: uid(g),
      relationship: 'grand-children',
      name: s(g?.name),
      age: nBounded(g?.age, 0, 120),
    })),
  ];
}

// "Self Employed" is a UI-only distinction (it relabels Employer as Business
// Name). Salesforce's employment status doesn't accept it, so it maps to
// "Employed" in the payload. Every other status passes through unchanged.
function payloadEmploymentStatus(status) {
  const v = s(status);
  return v === 'Self Employed' ? 'Employed' : v;
}

// Employed + Self Employed expose one field set in the Employment UI (job
// title, employer, years, planned retirement); Retired + Not Employed expose
// the other (last job, last employer, years worked). The status radio clears
// the hidden group in the store, but we guard here too so a status-inconsistent
// value can never reach Salesforce — whether left over from a status change or
// already stored on an older record from before that fix.
function isEmployedStatus(status) {
  const v = s(status);
  return v === 'Employed' || v === 'Self Employed';
}

function employmentRecord(uidStr, role, e) {
  // The Employment UI shows "Employed" pre-selected before any status is
  // stored, and fromSalesforce already reads a blank status back as
  // "Employed". This has to agree with both. Without the default, an advisor
  // who simply accepted the pre-selected radio (never clicking it, so nothing
  // was ever written) had jobTitle, employer, years and retirement age all
  // blanked on the way out, silently: they were guarded behind a status that
  // did not exist. That is "the employment data does not save" (Marcus
  // Gonzalez, 2026-08-05), and it is why it looked intermittent — it happened
  // only when nobody touched the radio.
  const status = s(e.status) || 'Employed';
  const employed = isEmployedStatus(status);
  return {
    uid: uidStr,
    employmentInfoFor: role,
    employmentStatus: payloadEmploymentStatus(status),
    // Only meaningful while still working — blanked for Retired / Not Employed.
    jobTitle: employed ? capS(s(e.jobTitle), 'PQ_Employment_Info__c.Job_Title__c') : '',
    employer: employed ? capS(s(e.employer), 'PQ_Employment_Info__c.Employer__c') : '',
    numOfYears: employed ? whole(n(e.yearsEmployed ?? e.numOfYears), 'PQ_Employment_Info__c.Number_of_Years__c') : null,
    // Sent for EVERY status: a retired client's retirement date is the date they
    // already retired, which advisors need to record (Jake, 2026-07-23). Blanking
    // it for Retired/Not Employed made the newly-visible field silently unsaveable.
    retirementDate: d(e.retirementDate),
    // nBounded, not bare n(). Retirement_Age__c is precision 3, and advisors
    // type the retirement YEAR into it: prod refused 2024 three times in two
    // minutes on 2026-08-31, and 1233 the day before, each taking the whole
    // record with it. The field sits directly beside Retirement Date, which is
    // where that number belongs, so this is a misread label rather than a slip.
    // The UI already marks it red past 120; the red never stopped the save.
    retirementAge: employed ? whole(nBounded(e.retirementAge, 0, 120), 'PQ_Employment_Info__c.Retirement_Age__c') : null,
    // Asked of Self Employed only, so a value left on a record that has since
    // been switched to plain Employed is stale and must not flow through.
    typeOfBusiness: status === 'Self Employed' ? s(e.typeOfBusiness) : '',
    // Only meaningful once no longer working — blanked for Employed / Self
    // Employed.
    lastJobTitle: employed ? '' : s(e.lastJob ?? e.lastJobTitle),
    lastEmployer: employed ? '' : s(e.lastEmployer),
    lastNumOfYears: employed ? null : whole(n(e.yearsWorked ?? e.lastNumOfYears), 'PQ_Employment_Info__c.Years_Worked__c'),
  };
}

// Other businesses the client or spouse owns or works for, beyond their main
// job ("I'm talking to a prospect who is the CEO of 4 companies", Blaine
// Thiederman, 2026-09-07). Each one is another employment record for that
// person. The key carries this marker so the load can tell them apart from the
// person's main job, and so the eMoney push, which sends one job per person,
// can leave them out.
export const OTHER_BUSINESS_KEY = '-emp-biz-';

const OTHER_BUSINESS_FIELDS = ['name', 'role', 'typeOfBusiness', 'numOfYears'];

function mapOtherBusinesses(data) {
  const hh = s(data._uid);
  const fam = data.family || {};
  const rows = Array.isArray(data.employment?.otherBusinesses) ? data.employment.otherBusinesses : [];
  return rows
    // A row with only an owner picked has nothing to save yet.
    .filter(r => r && OTHER_BUSINESS_FIELDS.some(k => s(r[k]) !== '') && uid(r))
    .map(r => ({
      uid: `${hh}${OTHER_BUSINESS_KEY}${uid(r)}`,
      // Salesforce files employment under the client or the spouse, so a row
      // that names neither (or is left blank) goes under the client.
      employmentInfoFor: ownerPerson(fam, r.owner) === 'client2' ? 'spouse' : 'client',
      employmentStatus: 'Employed',
      businessName: capS(s(r.name), 'PQ_Employment_Info__c.Business_Name__c'),
      jobTitle: capS(s(r.role), 'PQ_Employment_Info__c.Job_Title__c'),
      typeOfBusiness: capS(s(r.typeOfBusiness), 'PQ_Employment_Info__c.Type_of_Business__c'),
      numOfYears: whole(n(r.numOfYears), 'PQ_Employment_Info__c.Number_of_Years__c'),
    }));
}

function mapEmployment(data) {
  // External key for each employmentInfo record is derived from the household
  // id + ROLE, NOT the stored _uid. A Salesforce reload can mis-bucket the
  // stored _uid so client and spouse both end up '-emp-spouse', which then
  // collides on External_Key__c (DUPLICATE_VALUE on upsert). Role-derived keys
  // are always distinct and match what the first save created. Fall back to a
  // stored _uid only if the household id is somehow absent.
  const hh = s(data._uid);
  const e1 = data.employment?.client1 || {};
  const e2 = data.employment?.client2 || {};
  return [
    employmentRecord(hh ? `${hh}-emp-client` : s(e1._uid), 'client', e1),
    employmentRecord(hh ? `${hh}-emp-spouse` : s(e2._uid), 'spouse', e2),
    ...mapOtherBusinesses(data),
  ];
}

function mapProfessionals(data) {
  const rows = (Array.isArray(data.relationships?.professionals) ? data.relationships.professionals : []).filter(rowHasData);
  return rows.map(r => ({
    uid: uid(r),
    role: s(r?.role),
    name: s(r?.name),
    firm: s(r?.firm),
    city: s(r?.city),
    state: s(r?.state),
  }));
}

function mapLiabilities(data) {
  const rows = (Array.isArray(data.liabilities?.items) ? data.liabilities.items : []).filter(rowHasData);
  return rows.map(r => ({
    uid: uid(r),
    description: s(r?.description),
    type: s(r?.type),
    paymentPAndI: n(r?.paymentPAndI ?? r?.payment),
    amount: n(r?.amount),
    intRate: n(r?.intRate ?? r?.interestRate),
    termYears: n(r?.termYears ?? r?.term),
    // Per-row advisor note (the note icon). Pending Salesforce field.
    notes: s(r?.notes),
  }));
}

// The "asked, client has none" markers, as ONE comma-joined string, exactly how
// estatePlan is already sent. Recording these as policy rows would create
// phantom records in Salesforce and eMoney, which is the whole thing advisors
// asked us to avoid.
//
// A marker is dropped when the table actually holds a policy it covers. The UI
// already blocks that combination, but a marker ticked BEFORE the policy was
// added still sits in the store, and without this the payload would claim both
// "has an LTC policy" and "has no LTC".
//
// PENDING a Salesforce field: until Srivani adds one this rides along and is
// ignored on the far side, so markers persist locally and on the presented
// sheet but not yet on the record.
function mapInsuranceNoneOf(data) {
  const marked = Array.isArray(data?.insurance?.noneOf) ? data.insurance.noneOf : [];
  if (marked.length === 0) return '';
  const cfg = pqSections.find(sec => sec.id === 'insurance')?.noneMarkers;
  const rows = Array.isArray(data?.insurance?.policies) ? data.insurance.policies : [];
  const present = new Set(rows.map(r => s(r?.type).trim()).filter(Boolean));
  const covered = value =>
    (cfg?.options?.find(o => o.value === value)?.covers || []).some(t => present.has(t));
  return marked.filter(v => !covered(v)).join(',');
}

// The three core investment sections an advisor can mark "asked, client has
// none". A marker is dropped when that section actually holds accounts: the UI
// blocks the combination, but a marker set BEFORE accounts were added would
// otherwise reach Salesforce as a contradiction.
function mapInvestmentNoneOf(data) {
  const marked = Array.isArray(data?.assets?.noneOf) ? data.assets.noneOf : [];
  if (marked.length === 0) return '';
  const has = key => (Array.isArray(data?.assets?.[key]) ? data.assets[key] : []).some(rowHasData);
  return marked.filter(k => !has(k)).join(',');
}

function mapInsurance(data) {
  const rows = (Array.isArray(data.insurance?.policies) ? data.insurance.policies : []).filter(rowHasData);
  return rows.map(r => ({
    uid: uid(r),
    company: s(r?.company),
    type: s(r?.type),
    deathDailyBenefit: n(r?.benefit),
    insured: capS(s(r?.insured), 'PQ_Insurance__c.Insured__c'),
    owner: capS(s(r?.owner), 'PQ_Insurance__c.Owner__c'),
    policyDate: d(r?.policyDate),
    annualPremium: n(r?.annualPremium),
    cashValue: n(r?.cashValue),
    // The policy's original term as printed on it (eMoney counts it from the
    // policy date). Only a term policy has one, so a term typed in and then
    // left behind by a change of type does not follow the policy to eMoney.
    // Blank sends nothing and the push falls back to its default (P41).
    termYears: r?.type === 'Term Life' ? n(r?.termYears) : null,
    beneficiary: s(r?.beneficiary),
    // Per-row advisor note (the note icon). Pending Salesforce field.
    notes: s(r?.notes),
  }));
}

// `incomes` is now a single flat array. Each row carries a `type` discriminator.
function mapIncomes(data) {
  const i = data.income || {};
  const arr = key => (Array.isArray(i[key]) ? i[key] : []).filter(rowHasData);
  const out = [];

  arr('employment').forEach(r => {
    out.push({
      uid: uid(r),
      type: 'employmentIncome',
      owner: capS(s(r.owner), 'PQ_Income__c.Owner__c'),
      description: s(r.description),
      annualAmount: n(r.annualAmount),
      retirementDate: d(r.retirementDate),
      colaPercentage: n(r.colaPercentage ?? r.cola),
      yearType: s(r.yearType),
      // Notes were silently dropped from every income type (Sprint 40: "Income
      // obj notes not mapping") while expenses sent theirs — advisors could
      // type notes that never reached Salesforce.
      notes: s(r.notes),
    });
  });

  arr('socialSecurity').forEach(r => {
    out.push({
      uid: uid(r),
      type: 'socialSecurity',
      owner: capS(s(r.owner), 'PQ_Income__c.Owner__c'),
      description: s(r.description),
      annualAmount: n(r.annualAmount),
      startingAge: whole(nBounded(r.startingAge, 0, 120), 'PQ_Income__c.Starting_Age__c'),
      fullRetirementAge: whole(nBounded(r.fullRetAge, 0, 120), 'PQ_Income__c.Full_Retirement_Age__c'),
      colaPercentage: n(r.colaPercentage ?? r.cola),
      yearType: s(r.yearType),
      notes: s(r.notes),
    });
  });

  arr('pension').forEach(r => {
    out.push({
      uid: uid(r),
      type: 'pension',
      owner: capS(s(r.owner), 'PQ_Income__c.Owner__c'),
      description: s(r.description),
      annualAmount: n(r.annualAmount),
      startDate: d(r.startDate ?? r.retirementDate),
      // Starting age + full/partial-year indicator. No Salesforce fields yet
      // (pending with Srivani, like the 529 `state` field) — sent now so the
      // mapping lights up the moment they land; unknown keys are ignored by the
      // Function App.
      startAge: whole(nBounded(r.startAge, 0, 120), 'PQ_Income__c.Pension_Starting_Age__c'),
      survivorBenefit: n(r.survivorBenefit),
      colaPercentage: n(r.colaPercentage ?? r.cola),
      yearType: s(r.yearType),
      notes: s(r.notes),
    });
  });

  arr('other').forEach(r => {
    out.push({
      uid: uid(r),
      type: 'otherIncome',
      owner: capS(s(r.owner), 'PQ_Income__c.Owner__c'),
      description: s(r.description),
      annualAmount: n(r.annualAmount),
      startDate: d(r.startDate ?? r.retirementDate),
      endDate: d(r.endDate),
      colaPercentage: n(r.colaPercentage ?? r.cola),
      yearType: s(r.yearType),
      notes: s(r.notes),
    });
  });

  return out;
}

function mapTaxesAndExpenses(data) {
  const te = data.taxesExpenses || {};
  const expenses = Array.isArray(te.expenses) ? te.expenses : [];
  const rows = [];

  // Prior-year tax info (taxable income, deductions, federal/state/FICA) is now
  // sent on the Financial Profile node, not as separate rows here — those rows
  // had blank uids and caused a 500 on save.
  expenses.filter(rowHasData).forEach(r => {
    rows.push({
      uid: uid(r),
      category: s(r.category) || 'Annual Expense',
      subCategory: s(r.subCategory) || 'Living Expenses',
      description: s(r.description),
      amount: n(r.amount),
      startDate: d(r.startDate),
      endDate: d(r.endDate),
      colaPercentage: n(r.colaPercentage ?? r.cola),
      notes: s(r.notes),
    });
  });
  return rows;
}

// An asset row only earns a Salesforce record if it carries a financial value.
// A row with just a description (e.g. the "Primary Residence" placeholder the
// "Own your home?" checkbox spawns) is not an asset and must not persist as an
// empty child record (QA: orphan PQA-0555).
const ASSET_VALUE_FIELDS = [
  'marketValue', 'remainingLoan', 'costBasis', 'personalAdditions',
  'companyMatch', 'incomeEbit', 'incomeEBT', 'paymentPAndI', 'payment',
];
function assetRowHasValue(r) {
  if (!r || typeof r !== 'object') return false;
  return ASSET_VALUE_FIELDS.some(k => {
    const v = r[k];
    if (v == null || String(v).trim() === '') return false;
    return !isNaN(parseFloat(String(v).replace(/,/g, '')));
  });
}

function mapAssets(data) {
  const a = data.assets || {};
  const out = [];
  const noMatch = { companyMatch: null };
  const push = (rows, base, overrides = {}) => {
    if (!Array.isArray(rows)) return;
    rows.filter(assetRowHasValue).forEach(r => {
      out.push({ uid: uid(r), ...base, ...mapAssetRow(r), ...overrides });
    });
  };

  // Real estate carries its residence type in subCategory. fromSalesforce reads
  // it back to restore _autoKey, which drives the "Own your home?" toggle AND
  // the mortgage liability auto-row. Sending '' here dropped _autoKey on reload:
  // the home toggle silently unchecked and the mortgage auto-row was pruned,
  // inflating net worth. Map it per row from _autoKey so it round-trips.
  const RE_SUBCATEGORY = {
    ownHome: 'Primary Residence',
    secondaryHome: 'Secondary Residence',
    additionalRE: 'Additional Real Estate',
  };
  (Array.isArray(a.realEstate) ? a.realEstate : []).filter(assetRowHasValue).forEach(r => {
    out.push({
      uid: uid(r),
      category: 'Real Estate',
      subCategory: RE_SUBCATEGORY[r._autoKey] || '',
      ...mapAssetRow(r),
      ...noMatch,
    });
  });
  push(a.businessOther, { category: 'Business and Other', subCategory: 'Business Assets' }, noMatch);
  push(a.taxDeferred, { category: 'Investment Accounts', subCategory: 'Tax-Deferred Retirement' });
  push(a.roth, { category: 'Investment Accounts', subCategory: 'Tax-Free Roth' });
  push(a.taxable, { category: 'Investment Accounts', subCategory: 'Taxable Non-Retirement' }, noMatch);
  push(a.cashCd, { category: "Cash & CD's", subCategory: "Cash & CD's" }, noMatch);
  push(a.plan529, { category: '529 Plans', subCategory: '529 Plans' }, noMatch);
  // HSA keeps companyMatch: employer HSA contributions are real, the HSA table
  // exposes a Company Match column, and fromSalesforce reads it back (HSA
  // mirrors the Roth column set, and Roth keeps it too). It was swept into the
  // noMatch whitelist by mistake, so advisor-entered HSA matches were silently
  // discarded on save (Sprint 40: "Prod - HSA mapping").
  push(a.hsa, { category: 'HSA Accounts', subCategory: 'HSA Accounts' });

  return out;
}

function mapAssetRow(r) {
  return {
    description: s(r.description),
    marketValue: n(r.marketValue),
    remainingLoan: n(r.remainingLoan),
    intRate: n(r.intRate),
    term: cap(n(r.term), 'PQ_Asset__c.Term__c'),
    paymentPAndI: n(r.paymentPAndI ?? r.payment),
    yearAcquired: cap(n(r.yearAcquired), 'PQ_Asset__c.Year_Acquired__c'),
    costBasis: n(r.costBasis),
    ownership: s(r.ownership),
    incomeEbit: n(r.incomeEbit ?? r.incomeEBT),
    custodian: s(r.custodian),
    personalAdditions: n(r.personalAdditions),
    // Ticked when the client contributes the yearly maximum. The push then
    // sends the contribution as Maximum and eMoney tracks the limit, which
    // moves every year, instead of a dollar figure that goes stale (P40).
    maxContribution: r.maxContribution === true,
    companyMatch: n(r.companyMatch),
    type: capS(s(r.type), 'PQ_Asset__c.Type__c'),
    beneficiary: s(r.beneficiary),
    variableFixed: capS(s(r.variableFixed), 'PQ_Asset__c.Variable_Fixed__c'),
    issueDate: d(r.issueDate),
    // The year a Roth or taxable account was opened (Joe, 10/21). The field
    // exists in james; staging and production ignore the key until it is
    // created there (salesforce/PENDING-IN-STAGING.md).
    yearEstablished: cap(n(r.yearEstablished), 'PQ_Asset__c.Year_Established__c'),
    interestRate: n(r.interestRate),
    // 529 sponsoring state. No Salesforce field yet (pending with Srivani, like
    // Additional_Notes__c) — sent now so the mapping lights up the moment the
    // field lands. Unknown keys are ignored by the Function App.
    state: capS(s(r.state), 'PQ_Asset__c.State__c'),
    // Per-row advisor note (the note icon). Pending Salesforce field.
    notes: s(r.notes),
  };
}

// The handoff snapshot travels as one JSON string so it is a single Long Text
// Area on the Salesforce side. Anything malformed serializes to '' rather than
// throwing: a broken archive must never block a save.
function serializeArchive(archive) {
  if (!archive || typeof archive !== 'object' || !archive.at) return '';
  try {
    return JSON.stringify({ at: s(archive.at), by: s(archive.by), data: archive.data || {} });
  } catch {
    return '';
  }
}

function scrubNA(obj) {
  if (Array.isArray(obj)) return obj.map(scrubNA);
  if (obj && typeof obj === 'object') {
    const out = {};
    for (const k of Object.keys(obj)) out[k] = scrubNA(obj[k]);
    return out;
  }
  if (typeof obj === 'string' && (obj === 'N/A' || obj === 'n/a')) return '';
  return obj;
}

export function toSalesforce(data = {}) {
  const fam = data.family || {};
  const contact = data.contact || {};
  const goals = data.goals || {};
  const te = data.taxesExpenses || {};
  const m = getDashboardMetrics(data);

  // Match the PQ's "Total Expenses" exactly: Living Expenses (an annual figure)
  // + the annual expense rows. Previously livingExpenses was multiplied by 12,
  // which made SF's total disagree with what the advisor sees in the PQ.
  const annualExpenses = getTotalExpenses(data);
  const monthlyExpenses = annualExpenses / 12;

  // Completion %: portion of PQ sections that have any data filled in.
  //
  // "Any data" means data an advisor ENTERED. Rendering the form seeds one
  // blank { _uid } row into every empty table so none renders header-only, and
  // counting those as data meant an untouched section read as filled the moment
  // the form was opened — the percentage jumped on open with nobody typing,
  // which both sent Salesforce a wrong number and (because the save digest is
  // the payload) re-POSTed on every open. So `_`-prefixed bookkeeping keys and
  // rows containing nothing else don't count, at any depth.
  const hasRealData = v => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.some(hasRealData);
    if (typeof v === 'object') {
      return Object.entries(v).some(([k, inner]) => !k.startsWith('_') && hasRealData(inner));
    }
    return true;
  };
  const filledSections = pqSections.filter(sec => {
    const sd = data[sec.id];
    if (sd == null || typeof sd !== 'object') return false;
    return hasRealData(sd);
  }).length;
  const completionPct = Math.round(
    (filledSections / Math.max(pqSections.length, 1)) * 100
  );

  // Launched from a household that has no lead. The identity Salesforce
  // upserts on is the household (Account) Id instead of the Lead Id, and the
  // blocks Salesforce writes onto the household's own people — who they are
  // and how to reach them — are withheld entirely. See householdOwned.js for
  // why, and for the matching list the form locks.
  const householdId = s(data._householdId);
  const householdBlocks = householdId
    ? {}
    : {
        client: {
          firstName: s(fam.client1FirstName),
          lastName: s(fam.client1LastName),
          nickName: s(fam.client1Nickname),
          dateOfBirth: d(fam.client1DOB),
          age: nBounded(fam.client1Age, 0, 120),
        },
        spouse: {
          firstName: s(fam.client2FirstName),
          lastName: s(fam.client2LastName),
          nickName: s(fam.client2Nickname),
          dateOfBirth: d(fam.client2DOB),
          age: nBounded(fam.client2Age, 0, 120),
          yearsMarried: nBounded(fam.yearsMarried, 0, 99),
        },
        contactInfo: {
          streetAddress: s(contact.address1),
          addressLine2: s(contact.address2),
          city: s(contact.city),
          state: s(contact.state),
          zipCode: s(contact.zip),
          clientHomePhone: s(contact.homePhone),
          clientMobilePhone: s(contact.client1Cell),
          clientEmail: s(contact.client1Email),
          spouseMobilePhone: s(contact.client2Cell),
          spouseEmail: s(contact.client2Email),
          referredBy: s(contact.referredBy),
        },
      };

  return scrubNA({
    // FinancialProfileService upserts on the Lead's native Id, so leadId is the
    // only identity field needed (Salesforce no longer uses External_Key__c).
    // A household launch has no lead at all, so it sends householdId instead
    // and Salesforce hangs the Financial Profile off the household.
    ...(householdId ? { householdId } : { leadId: s(data._uid) }),
    pqIntakeFormcompletionPercentage: s(completionPct),
    ...householdBlocks,
    financialProfile: {
      uid: s(data._uid),
      // Prior-year tax info lives on the Financial Profile (not as separate
      // taxesAndExpenses rows, which had blank uids and 500'd on save).
      taxableIncome: n(te.taxableIncome),
      standardItemDeduction: n(te.standardItemDeduction),
      // Which basis the tax figures are on ("Prior Year Actual" /
      // "Current Year Estimate") and whether the deduction is Standard or
      // Itemized (Jake, 2026-07-23). No Salesforce fields yet — pending with
      // Srivani, sent now so the mapping lights up the moment they land. The
      // itemized AMOUNT reuses the existing standardItemDeduction field above.
      taxBasis: s(te.taxBasis),
      deductionType: s(te.deductionType),
      capLossCarryForward: n(te.capLossCarryForward),
      federalTax: n(te.federalTax),
      stateTax: n(te.stateTax),
      ficaTax: n(te.ficaTax),
      livingExpenses: n(te.livingExpenses),
      // The note beside Living Expenses. No Salesforce field yet (Srivani) —
      // sent now so the mapping lights up the moment it lands. Ask for a Long
      // Text Area, not Text: this is a free-text note in the same family as
      // additionalNotes/clientGoals/advisorNotes, and 255 characters does not
      // truncate quietly on that family, it fails the save outright.
      livingExpensesNotes: s(te.livingExpensesNotes),
      totalIncome: n(m.totalIncome),
      status: '',
      totalExpenses: n(annualExpenses),
      totalAnnualExpenses: n(annualExpenses),
      totalAssets: n(m.totalAssets),
      totalLiabilities: n(m.totalLiabilities),
      totalMonthlyExpenses: n(monthlyExpenses),
      netWorth: n(m.totalNetWorth),
      yearlyCashFlow: n(m.cashFlow),
      // Renting checkbox + monthly rent (Assets section). The derived rent
      // *expense* line item already round-trips via the Taxes & Expenses
      // auto-sync; these two are the raw inputs that produce it, sent now so
      // the mapping lights up once Is_Renting__c / Monthly_Rent__c exist on
      // Financial_Profile__c (see PQ backend follow-ups doc).
      isRenting: bool(data?._assetChecks?.renting),
      monthlyRent: n(data?.assets?.rentMonthly),
      // Free-text catch-all at the end of Investment Accounts. Sent now so it
      // round-trips once Additional_Account_Info__c exists on
      // Financial_Profile__c (Srivani).
      additionalAccountInfo: s(data?.assets?.additionalAccountInfo),
      // The handoff snapshot: the PQ as it stood when the advisor submitted
      // the Plan Order Form, frozen as one JSON string {at, by, data}. It is
      // the baseline the planning change log is measured against, and the
      // archived copy Eugene asked for — the app re-renders it through Present
      // mode, so archiving needs no document. Long Text Area pending with
      // Srivani; until it lands this is sent and dropped, like the others.
      pqSnapshot: serializeArchive(data?.pqArchive),
      // The planning change log: who changed what since the advisor handed
      // off, appended by planning-mode saves (see StoreContext). One JSON
      // string, one Long Text Area for Srivani, same pattern as the snapshot.
      planningChangeLog: serializeLog(data?.planningLog),
      family: {
        noOfChildren: whole(nBounded(fam.numChildren, 0, 99), 'Financial_Profile__c.Number_of_Children__c'),
        noOfGrandchildren: whole(nBounded(fam.numGrandchildren, 0, 99), 'Financial_Profile__c.Number_of_Grandchildren__c'),
        additionalFamilyInformation: s(fam.additionalFamilyInfo),
        children: mapChildren(data),
      },
      goals: {
        clientGoals: s(goals.clientGoals),
        additionalNotes: s(goals.additionalNotes),
        advisorNotes: s(goals.advisorNotes),
      },
      estatePlan: {
        estatePlan: Array.isArray(goals.estatePlan) ? goals.estatePlan.join(',') : s(goals.estatePlan),
        yearsEstablished: cap(n(goals.yearsEstablished), 'Financial_Profile__c.Estate_Plan_Years_Established__c'),
      },
      employmentInfo: mapEmployment(data),
      professionalRelationships: mapProfessionals(data),
      liabilities: mapLiabilities(data),
      insurance: mapInsurance(data),
      // "Asked, client has none" markers, as ONE comma-joined string, exactly
      // how estatePlan is already sent. Recording these as policy rows would
      // create phantom records in Salesforce and eMoney, which is the whole
      // thing advisors asked us to avoid. PENDING a Salesforce field: until
      // Srivani adds one this rides along and is ignored on the far side, so
      // the markers persist locally and on the presented sheet but not yet on
      // the record.
      insuranceNoneOf: mapInsuranceNoneOf(data),
      // Same construction for the three core investment sections: one
      // comma-joined string, and a marker is dropped if the section actually
      // holds accounts, so the record cannot claim both. PENDING a Salesforce
      // field (Srivani), like insuranceNoneOf.
      investmentNoneOf: mapInvestmentNoneOf(data),
      incomes: mapIncomes(data),
      taxesAndExpenses: mapTaxesAndExpenses(data),
      assets: mapAssets(data),
    },
  });
}
